import PropTypes from "prop-types"
import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react"
import { Badge } from "reactstrap"
import { Box } from "@mui/material"
import { Link } from "react-router-dom"
import { withTranslation } from "react-i18next"
import moment from "moment"
import { apiBuscarFastfoodMobileConfigs } from "../../helpers/backend_helper"
import ListDataTable from "../../components/Common/list-data-table"
import { describeLimiteLicenca, describeSaldoLicenca, isLimiteLicencaExcedido } from "./const/fastfoodMobileLicenca"

const describeDateTime = dateStr => dateStr ? moment(dateStr).format("DD/MM/YY HH:mm") : ""

const describeAtivo = (val, t) => val === "S"
  ? { label: t("fastfoodmobile.config.enum.status.ativo"), color: "success" }
  : { label: t("fastfoodmobile.config.enum.status.inativo"), color: "danger" }

const describeAmbiente = (val, t) => {
  if (!val) return null
  const key = `fastfoodmobile.config.enum.ambiente.${val.toLowerCase()}`
  const translated = t(key)
  return translated === key ? val : translated
}

const FastfoodMobileConfigTable = forwardRef((props, refFather) => {
  const ref = useRef()
  const { t, filters, usePermissionEdit, onVisualizar, onEditar } = props

  const [data, setData] = useState([])

  useImperativeHandle(refFather, () => ({
    search() { ref?.current?.search() }
  }))

  const colunas = useMemo(() => [
    { header: t("fastfoodmobile.config.lista.coluna.idempresa"),
      accessorKey: "idEmpresa", size: 110,
      muiTableBodyCellProps: { align: "right" } },
    { header: t("fastfoodmobile.config.lista.coluna.empresa"),
      accessorKey: "empresa_RazaoSocial", size: 220, enableColumnFilter: false },
    { header: t("fastfoodmobile.config.lista.coluna.cnpj"),
      accessorKey: "cnpj_sem_mascara", size: 160, filterFn: "contains" },
    { header: t("fastfoodmobile.config.lista.coluna.ativo"),
      accessorKey: "ativo", size: 110, enableColumnFilter: false,
      Cell: ({ cell }) => {
        const { label, color } = describeAtivo(cell.getValue(), t)
        return <Badge className={ `font-size-12 badge-soft-${color}` } color={ color } pill>{ label }</Badge>
      } },
    { header: t("fastfoodmobile.config.lista.coluna.ambiente"),
      accessorKey: "ambiente", size: 130, enableColumnFilter: false,
      Cell: ({ cell }) => describeAmbiente(cell.getValue(), t) },
    { header: t("fastfoodmobile.config.lista.coluna.licenca-dispositivos"),
      accessorKey: "licenca_qtd_dispositivos", size: 150, enableColumnFilter: false,
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => {
        const { label, color } = describeLimiteLicenca(cell.getValue())
        return <Badge className={ `font-size-12 badge-soft-${color}` } color={ color } pill>{ label }</Badge>
      } },
    { header: t("fastfoodmobile.config.lista.coluna.licenca-dispositivos-ativos"),
      accessorKey: "licenca_qtd_dispositivos_ativos", size: 130, enableColumnFilter: false, enableSorting: false,
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell, row }) => {
        const excedido = isLimiteLicencaExcedido(row.original.licenca_qtd_dispositivos, cell.getValue())
        const color = excedido ? "danger" : "success"
        return <Badge className={ `font-size-12 badge-soft-${color}` } color={ color } pill>{ cell.getValue() ?? 0 }</Badge>
      } },
    { header: t("fastfoodmobile.config.lista.coluna.licenca-dispositivos-inativos"),
      accessorKey: "licenca_qtd_dispositivos_inativos", size: 130, enableColumnFilter: false, enableSorting: false,
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell }) => (
        <Badge className="font-size-12 badge-soft-secondary" color="secondary" pill>{ cell.getValue() ?? 0 }</Badge>
      ) },
    { header: t("fastfoodmobile.config.lista.coluna.licenca-dispositivos-saldo"),
      accessorKey: "licenca_qtd_dispositivos_saldo", size: 170, enableColumnFilter: false, enableSorting: false,
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ cell, row }) => {
        const { label, color } = describeSaldoLicenca(row.original.licenca_qtd_dispositivos, cell.getValue())
        return <Badge className={ `font-size-12 badge-soft-${color}` } color={ color } pill>{ label }</Badge>
      } },
    { header: t("fastfoodmobile.config.lista.coluna.datainclusao"),
      accessorKey: "created_at", size: 150, enableColumnFilter: false,
      sortingFn: "datetime",
      Cell: ({ cell }) => describeDateTime(cell.getValue()) },
    { header: t("fastfoodmobile.config.lista.coluna.dataatualizacao"),
      accessorKey: "updated_at", size: 150, enableColumnFilter: false,
      sortingFn: "datetime",
      Cell: ({ cell }) => describeDateTime(cell.getValue()) }
  ], [t])

  return (
    <ListDataTable
      ref={ ref }
      searchFn={ params => apiBuscarFastfoodMobileConfigs(params) }
      advancedFilters={ filters }
      columns={ colunas }
      data={ data }
      setData={ setData }
      enableRowActions={ true }
      positionActionsColumn="last"
      defaultSorting={ [{ id: "id", desc: true }] }
      renderRowActions={ row => (
        <Box sx={ { display: "flex", gap: "0.5rem" } }>
          <Link to="#" className="text-primary"
                title={ t("fastfoodmobile.config.lista.acao.visualizar") }>
            <i className="mdi mdi-eye font-size-18 ms-1"
               onClick={ e => { e.preventDefault(); onVisualizar(row.original) } } />
          </Link>
          { usePermissionEdit && (
            <Link to="#" className="text-primary"
                  title={ t("fastfoodmobile.config.lista.acao.editar") }>
              <i className="mdi mdi-pencil font-size-18 ms-1"
                 onClick={ e => { e.preventDefault(); onEditar(row.original) } } />
            </Link>
          ) }
        </Box>
      ) }
    />
  )
})

FastfoodMobileConfigTable.propTypes = {
  t: PropTypes.func,
  filters: PropTypes.array,
  usePermissionEdit: PropTypes.bool,
  onVisualizar: PropTypes.func.isRequired,
  onEditar: PropTypes.func.isRequired
}

export default withTranslation()(FastfoodMobileConfigTable)
